import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, ThumbsUp, MessageSquare } from 'lucide-react';

interface UserReviewsProps {
  toolId: string;
}

export const UserReviews: React.FC<UserReviewsProps> = ({ toolId }) => {
  const [reviews] = useState([
    {
      id: '1',
      author: 'Alex Johnson',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=100&auto=format&fit=crop',
      rating: 5,
      title: 'Game changer for my workflow',
      content: 'I\'ve been using this tool for about 3 months now and it has completely transformed how I work. The interface is intuitive and the AI capabilities are impressive.',
      date: '2025-03-15',
      helpful: 24
    },
    {
      id: '2',
      author: 'Sarah Miller',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=100&auto=format&fit=crop',
      rating: 4,
      title: 'Great tool with minor issues',
      content: 'Overall this is an excellent AI tool that has saved me countless hours. There are a few bugs here and there, but the development team is responsive and constantly improving.',
      date: '2025-02-28',
      helpful: 18
    },
    {
      id: '3',
      author: 'Michael Chen',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=100&auto=format&fit=crop',
      rating: 5,
      title: 'Worth every penny',
      content: 'The subscription fee is absolutely worth it for the time savings alone. I was skeptical at first but now I can\'t imagine working without it.',
      date: '2025-02-10',
      helpful: 32
    }
  ]);

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">User Reviews</h2>
        
        <div className="space-y-6">
          {reviews.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0 last:pb-0"
            >
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full overflow-hidden mr-4 bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                  <img 
                    src={review.avatar} 
                    alt={`${review.author}'s avatar`} 
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100 mr-2">{review.author}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{new Date(review.date).toLocaleDateString()}</span>
                  </div>
                  
                  <div className="flex mb-2">
                    {[1, 2, 3, 4, 5].map(star => (
                      <Star
                        key={star}
                        size={16}
                        className={`${
                          star <= review.rating
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300 dark:text-gray-600'
                        } mr-0.5`}
                      />
                    ))}
                  </div>
                  
                  <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-1">{review.title}</h5>
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-3">{review.content}</p>
                  
                  <div className="flex items-center text-sm">
                    <button className="inline-flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                      <ThumbsUp size={14} className="mr-1" />
                      Helpful ({review.helpful})
                    </button>
                    <span className="mx-2 text-gray-300 dark:text-gray-600">|</span>
                    <button className="inline-flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                      <MessageSquare size={14} className="mr-1" />
                      Reply
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Write a Review */}
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Write a Review</h3>
          <form className="space-y-4">
            <div>
              <label htmlFor="rating" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Rating
              </label>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className="text-gray-300 dark:text-gray-600 hover:text-yellow-400 focus:outline-none"
                  >
                    <Star size={24} className="mr-1" />
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Review Title
              </label>
              <input
                type="text"
                id="title"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Summarize your experience"
              />
            </div>
            
            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Review Content
              </label>
              <textarea
                id="content"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Share your experience with this tool"
              ></textarea>
            </div>
            
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md transition-colors"
            >
              Submit Review
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};